import http from "../../axiosSetup";
import {
  EmailAuthProvider,
  getAuth,
  linkWithPopup,
  OAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  unlink,
  updatePassword,
} from "firebase/auth";
import ClaimCheckService from "../ClaimCheckService";
import { firebaseApp } from "config/firebase";
import OutputError from "config/OutputError";
import { clearSWRCache } from "config/swr";
import AnalyticsService from "services/AnalyticsService/AnalyticsService";

const microsoftProvider = new OAuthProvider("microsoft.com");

class UserService {
  static getAuth() {
    return getAuth(firebaseApp);
  }

  static get currentUser() {
    return UserService.getAuth().currentUser;
  }

  static getMe() {
    return http.get("/users/me").then((response) => response.data);
  }

  static setLastSeen() {
    return http.post("/users/me/last-seen").then((response) => response.data);
  }

  /**
   * @param {any} profile
   */
  static updateProfile({ id, name, organisation }) {
    return http.put("/users/me", { id, name, organisation }).then((response) => response.data);
  }

  /**
   * @param {{ newPassword: string }} param1
   * @returns {Promise<void>}
   */
  static async updatePassword({ newPassword }) {
    return updatePassword(UserService.getAuth().currentUser, newPassword);
  }

  /**
   * @param {string} password
   * @returns {Promise<void>} */
  static reauthenticateWithPassword(password) {
    const credential = EmailAuthProvider.credential(UserService.getAuth()?.currentUser?.email, password);
    return reauthenticateWithCredential(UserService.getAuth().currentUser, credential);
  }

  static signInWithCustomToken(customLoginToken) {
    return signInWithCustomToken(UserService.getAuth(), customLoginToken);
  }

  static onAuthStateChanged(onChange) {
    return UserService.getAuth().onAuthStateChanged((user) => onChange(user));
  }

  /**
   * @param {{ name: string; contentType: string; content: string | ArrayBuffer; }} photo
   */
  static setProfileImage(photo) {
    return http.patch("/users/me/profile-image", photo).then((response) => response.data);
  }

  static deleteProfileImage() {
    return http.delete("/users/me/profile-image").then((response) => response.data);
  }

  static toggleImperialSystem(imperialSystemEnabled) {
    return http
      .patch("/users/me/preferences/imperial-system", { imperialSystemEnabled })
      .then((response) => response.data);
  }

  /**
   * @param {string} timeZone
   * @returns {Promise<import("types").UserProfile["preferences"]>} */
  static updateTimeZone(timeZone) {
    return http.patch("/users/me/preferences/time-zone", { timeZone }).then((response) => response.data);
  }

  static setDefaultRAGTemplate(defaultRagStatusTemplateId) {
    return http
      .patch("/users/me/preferences/default-rag-status-template", { defaultRagStatusTemplateId })
      .then((response) => response.data);
  }
  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  static setDefaultDashboard(id) {
    return http.patch("/users/me/preferences/default-dashboard", { id }).then((response) => response.data);
  }

  static async validateSigninClaims() {
    try {
      if (!(await UserService.getOrganisationId())) {
        throw new OutputError({ code: "output_auth/organisationId-unset-on-user-claims" });
      }

      const osRole = await UserService.getRole();

      if (osRole === ClaimCheckService.roles.ATHLETE) {
        throw new OutputError({ code: "output_auth/athlete-role-not-allowed" });
      } else if (osRole === ClaimCheckService.roles.OWNER && !(await UserService.checkHubUser())) {
        throw new OutputError({ code: "output_auth/hubUser-feature-not-enabled" });
      }
    } catch (error) {
      await UserService.signOut();
      throw error;
    }
  }

  static async signIn(email, password) {
    const userCredential = await signInWithEmailAndPassword(UserService.getAuth(), email, password);

    return userCredential.user;
  }

  static signInWithMicrosoft() {
    return signInWithPopup(getAuth(), microsoftProvider);
  }

  static linkMicrosoftSignIn() {
    return linkWithPopup(UserService.getAuth().currentUser, microsoftProvider);
  }

  static unlinkMicrosoftSignIn() {
    return unlink(UserService.getAuth().currentUser, microsoftProvider.providerId);
  }

  static signOut() {
    AnalyticsService.clear();
    if (UserService.currentUser) clearSWRCache(); // Clear SWR cache (useQuery cached data)
    return UserService.getAuth().signOut();
  }

  static sendPasswordResetEmail(email) {
    return sendPasswordResetEmail(UserService.getAuth(), email);
  }

  /**
   * @returns {Promise<import("types/organisation").OrganisationRole>} */
  static async getRole() {
    const claims = await ClaimCheckService.getClaims();

    if (!claims.osRole) throw new OutputError({ code: "output_auth/osRole-unset-on-user-claims" });

    return claims.osRole;
  }

  static async getOrganisationId() {
    const claims = await ClaimCheckService.getClaims();

    if (!claims.organisationId) throw new OutputError({ code: "output_auth/organisationId-unset-on-user-claims" });

    return claims.organisationId;
  }

  static async checkHubUser() {
    const claims = await ClaimCheckService.getClaims();

    return claims.hubUser;
  }
}
export default UserService;
